import { useState, useEffect } from 'react'
import supabase from '../../lib/supabase'
import * as FiIcons from 'react-icons/fi'
import SafeIcon from '../../common/SafeIcon'

function ServicesManager() {
  const [services, setServices] = useState([])
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingService, setEditingService] = useState(null)

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    icon: '',
    price: '',
    image_url: '',
    category_id: '',
    sort_order: 0,
    show_in_carousel: false,
    active: true,
    features: []
  })

  useEffect(() => {
    fetchServices()
    fetchCategories()
  }, [])

  const fetchServices = async () => {
    try {
      setLoading(true)
      setError(null)

      const { data, error } = await supabase
        .from('codevaro_services')
        .select(`
          *,
          codevaro_categories!fk_category (
            id,
            name,
            slug
          )
        `)
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: false })

      if (error) throw error

      setServices(data || [])
    } catch (error) {
      console.error('Error fetching services:', error)
      setError('Σφάλμα κατά τη φόρτωση των υπηρεσιών')
    } finally {
      setLoading(false)
    }
  }

  const fetchCategories = async () => {
    try {
      const { data, error } = await supabase
        .from('codevaro_categories')
        .select('id, name, slug')
        .order('name', { ascending: true })

      if (error) throw error

      setCategories(data || [])
    } catch (error) {
      console.error('Error fetching categories:', error)
    }
  }

  const resetForm = () => {
    setEditingService(null)
    setFormData({
      title: '',
      description: '',
      icon: '',
      price: '',
      image_url: '',
      category_id: '',
      sort_order: 0,
      show_in_carousel: false,
      active: true,
      features: []
    })
  }

  const openAddModal = () => {
    resetForm()
    setIsModalOpen(true)
  }

  const openEditModal = (service) => {
    setEditingService(service)
    setFormData({
      title: service.title || '',
      description: service.description || '',
      icon: service.icon || '',
      price: service.price ?? '',
      image_url: service.image_url || '',
      category_id: service.category_id || '',
      sort_order: service.sort_order || 0,
      show_in_carousel: !!service.show_in_carousel,
      active: service.active !== false,
      features: Array.isArray(service.features) ? service.features : []
    })
    setIsModalOpen(true)
  }

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target

    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
  }

  const handleFeaturesChange = (e) => {
    const features = e.target.value
      .split('\n')
      .map((item) => item.trim())
      .filter(Boolean)

    setFormData((prev) => ({
      ...prev,
      features
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    try {
      setLoading(true)
      setError(null)

      const payload = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        icon: formData.icon?.trim() || null,
        price: formData.price === '' ? null : Number(formData.price),
        image_url: formData.image_url?.trim() || null,
        category_id: formData.category_id || null,
        sort_order: Number(formData.sort_order) || 0,
        show_in_carousel: !!formData.show_in_carousel,
        active: !!formData.active,
        features: Array.isArray(formData.features) ? formData.features : []
      }

      if (editingService) {
        const { error } = await supabase
          .from('codevaro_services')
          .update(payload)
          .eq('id', editingService.id)

        if (error) throw error
      } else {
        const { error } = await supabase
          .from('codevaro_services')
          .insert([payload])

        if (error) throw error
      }

      setIsModalOpen(false)
      resetForm()
      await fetchServices()
    } catch (error) {
      console.error('Error saving service:', error)
      setError('Σφάλμα κατά την αποθήκευση της υπηρεσίας')
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (id) => {
    if (!window.confirm('Είστε σίγουροι ότι θέλετε να διαγράψετε αυτή την υπηρεσία;')) {
      return
    }

    try {
      setLoading(true)

      const { error } = await supabase
        .from('codevaro_services')
        .delete()
        .eq('id', id)

      if (error) throw error

      await fetchServices()
    } catch (error) {
      console.error('Error deleting service:', error)
      setError('Σφάλμα κατά τη διαγραφή της υπηρεσίας')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div>
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-gray-900">Υπηρεσίες</h1>

        <button
          onClick={openAddModal}
          className="inline-flex items-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700"
        >
          <SafeIcon icon={FiIcons.FiPlus} className="mr-2" />
          Νέα Υπηρεσία
        </button>
      </div>

      {error ? (
        <div className="mt-4 rounded border border-red-400 bg-red-100 px-4 py-3 text-red-700">
          {error}
        </div>
      ) : null}

      <div className="mt-6">
        {loading && !isModalOpen ? (
          <div className="flex justify-center">
            <SafeIcon icon={FiIcons.FiLoader} className="h-8 w-8 animate-spin text-blue-500" />
          </div>
        ) : (
          <div className="overflow-hidden bg-white shadow sm:rounded-lg">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500">
                    Τίτλος
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500">
                    Κατηγορία
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500">
                    Τιμή
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500">
                    Carousel
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500">
                    Ενεργή
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium uppercase text-gray-500">
                    Ενέργειες
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-200 bg-white">
                {services.map((service) => (
                  <tr key={service.id}>
                    <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900">
                      {service.title}
                    </td>

                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                      {service.codevaro_categories?.name || '-'}
                    </td>

                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                      {service.price ?? '-'}
                    </td>

                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                      {service.show_in_carousel ? 'Ναι' : 'Όχι'}
                    </td>

                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                      {service.active ? 'Ναι' : 'Όχι'}
                    </td>

                    <td className="space-x-3 whitespace-nowrap px-6 py-4 text-right text-sm font-medium">
                      <button
                        onClick={() => openEditModal(service)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        Επεξεργασία
                      </button>

                      <button
                        onClick={() => handleDelete(service.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Διαγραφή
                      </button>
                    </td>
                  </tr>
                ))}

                {services.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-10 text-center text-sm text-gray-500">
                      Δεν υπάρχουν υπηρεσίες.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {isModalOpen ? (
        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-screen items-end justify-center px-4 pb-20 pt-4 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 opacity-75"></div>

            <span className="hidden sm:inline-block sm:h-screen sm:align-middle">
              &#8203;
            </span>

            <div className="inline-block transform overflow-hidden rounded-lg bg-white text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl sm:align-middle">
              <form onSubmit={handleSubmit}>
                <div className="space-y-4 bg-white px-4 pb-4 pt-5 sm:p-6">
                  <h3 className="text-lg font-medium text-gray-900">
                    {editingService ? 'Επεξεργασία Υπηρεσίας' : 'Νέα Υπηρεσία'}
                  </h3>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Τίτλος
                    </label>
                    <input
                      name="title"
                      required
                      value={formData.title}
                      onChange={handleChange}
                      className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Περιγραφή
                    </label>
                    <textarea
                      name="description"
                      rows={4}
                      required
                      value={formData.description}
                      onChange={handleChange}
                      className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                    />
                  </div>

                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Κατηγορία
                      </label>
                      <select
                        name="category_id"
                        value={formData.category_id}
                        onChange={handleChange}
                        className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                      >
                        <option value="">-- Επιλέξτε κατηγορία --</option>
                        {categories.map((category) => (
                          <option key={category.id} value={category.id}>
                            {category.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Τιμή
                      </label>
                      <input
                        name="price"
                        type="number"
                        step="0.01"
                        value={formData.price}
                        onChange={handleChange}
                        className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Icon
                      </label>
                      <input
                        name="icon"
                        value={formData.icon}
                        onChange={handleChange}
                        className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Image URL
                      </label>
                      <input
                        name="image_url"
                        value={formData.image_url}
                        onChange={handleChange}
                        className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Sort Order
                      </label>
                      <input
                        name="sort_order"
                        type="number"
                        value={formData.sort_order}
                        onChange={handleChange}
                        className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Features (μία ανά γραμμή)
                    </label>
                    <textarea
                      value={(formData.features || []).join('\n')}
                      onChange={handleFeaturesChange}
                      rows={5}
                      className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                    />
                  </div>

                  <div className="flex items-center gap-6">
                    <label className="inline-flex items-center">
                      <input
                        type="checkbox"
                        name="show_in_carousel"
                        checked={formData.show_in_carousel}
                        onChange={handleChange}
                        className="mr-2"
                      />
                      Στο carousel
                    </label>

                    <label className="inline-flex items-center">
                      <input
                        type="checkbox"
                        name="active"
                        checked={formData.active}
                        onChange={handleChange}
                        className="mr-2"
                      />
                      Ενεργή
                    </label>
                  </div>
                </div>

                <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                  <button
                    type="submit"
                    disabled={loading}
                    className="inline-flex w-full justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:opacity-50 sm:ml-3 sm:w-auto"
                  >
                    Αποθήκευση
                  </button>

                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-gray-700 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                  >
                    Ακύρωση
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}

export default ServicesManager